import { useCallback, useEffect, useRef, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api, asArray, formatApiError } from "@/lib/api";
import { fmtDate, fmtDateTime } from "@/lib/time";
import { ProblemStateBadge, SeverityBadge, StatusBadge } from "@/components/Primitives";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { ArrowLeft, CheckCircle2, Search, Plus, Unlink } from "lucide-react";
import { toast } from "sonner";

const WRITE_ROLES = ["l2_technician", "l3_technician", "service_desk_manager", "super_admin", "msp_owner"];

export default function ProblemDetail() {
  const { id } = useParams();
  const [problem, setProblem] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [states, setStates] = useState([]);
  const [me, setMe] = useState(null);
  const [draft, setDraft] = useState({ title: "", state: "", root_cause: "", workaround: "" });
  const [saving, setSaving] = useState(false);
  const [resolveOpen, setResolveOpen] = useState(false);

  const [ticketQuery, setTicketQuery] = useState("");
  const [ticketResults, setTicketResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [linkBusy, setLinkBusy] = useState(null);

  useEffect(() => { api.get("/auth/me").then((r) => setMe(r.data)).catch(() => {}); }, []);
  useEffect(() => {
    api.get("/desk/problem-states").then((r) => setStates(asArray(r.data))).catch(() => setStates([]));
  }, []);
  const mayWrite = !!me && WRITE_ROLES.includes(me.role);

  const load = useCallback(async () => {
    setLoading(true);
    setError(false);
    try {
      const r = await api.get(`/problems/${id}`);
      setProblem(r.data && typeof r.data === "object" ? r.data : null);
    } catch {
      setError(true);
    } finally { setLoading(false); }
  }, [id]);

  useEffect(() => { load(); }, [load]);

  // Reset the edit draft only when the RECORD ITSELF changes (a fresh id),
  // never merely because `problem` was replaced with a new object reference.
  // This used to be keyed on `problem` directly, on the theory that load()
  // only ever runs after mount or after one of our own actions completes --
  // which is true, but irrelevant: linkTicket()/unlinkTicket() below both
  // used to call the full load() on success, handing back a NEW `problem`
  // object for the SAME record, and that alone re-ran this effect and wiped
  // an in-progress edit (title/root_cause/workaround) with zero warning. A
  // technician typing a root-cause write-up who links a related ticket
  // before saving lost the write-up the moment the "Ticket linked" toast
  // fired. Comparing ids via a ref -- rather than depending on the id alone,
  // which would itself violate exhaustive-deps -- makes a reload of the same
  // record a no-op here regardless of how much of `problem` changed.
  const draftForId = useRef(null);
  useEffect(() => {
    if (problem && draftForId.current !== problem.id) {
      draftForId.current = problem.id;
      setDraft({
        title: problem.title || "", state: problem.state || "",
        root_cause: problem.root_cause || "", workaround: problem.workaround || "",
      });
    }
  }, [problem]);

  // link-ticket/unlink-ticket respond via the PATCH-shaped problem doc, which
  // does NOT carry the hydrated `linked_tickets` array (only GET /problems/{id}
  // hydrates that) -- so a fresh GET is still needed. But replacing the whole
  // `problem` object here is exactly the hazard above: merge in only the two
  // fields these actions actually affect, so title/state/root_cause/workaround
  // -- and therefore the unsaved draft mirroring them -- are never touched by
  // a linking action.
  const refreshLinkedTickets = useCallback(async () => {
    try {
      const r = await api.get(`/problems/${id}`);
      const fresh = r.data;
      if (fresh && typeof fresh === "object") {
        setProblem((prev) => (prev
          ? { ...prev, linked_tickets: fresh.linked_tickets, linked_ticket_ids: fresh.linked_ticket_ids }
          : fresh));
      }
    } catch {
      // Best-effort refresh only -- the link/unlink action itself already
      // succeeded or failed and told the user so via its own toast.
    }
  }, [id]);

  const currentStateRow = states.find((s) => s.key === problem?.state);
  const isResolved = !!currentStateRow?.terminal;
  // A terminal state is refused by PATCH (422, pointing at /resolve) -- so it
  // must never be an option in this picker to begin with.
  const editableStates = states.filter((s) => !s.terminal);

  async function save() {
    if (!draft.title.trim()) return toast.error("A title is required.");
    setSaving(true);
    try {
      await api.patch(`/problems/${id}`, {
        title: draft.title.trim(), state: draft.state,
        root_cause: draft.root_cause, workaround: draft.workaround,
      });
      await load();
      toast.success("Problem updated");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally { setSaving(false); }
  }

  useEffect(() => {
    if (!ticketQuery.trim()) { setTicketResults([]); return; }
    setSearching(true);
    const t = setTimeout(() => {
      api.get("/tickets", { params: { q: ticketQuery, limit: 8 } })
        .then((r) => setTicketResults(asArray(r.data?.items)))
        .catch(() => setTicketResults([]))
        .finally(() => setSearching(false));
    }, 300);
    return () => clearTimeout(t);
  }, [ticketQuery]);

  const linkedIds = new Set(problem?.linked_ticket_ids || []);
  const visibleResults = ticketResults.filter((t) => !linkedIds.has(t.id));

  async function linkTicket(ticketId) {
    setLinkBusy(ticketId);
    try {
      await api.post(`/problems/${id}/link-ticket`, { ticket_id: ticketId });
      setTicketQuery("");
      setTicketResults([]);
      await refreshLinkedTickets();
      toast.success("Ticket linked");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally { setLinkBusy(null); }
  }

  async function unlinkTicket(ticketId) {
    setLinkBusy(ticketId);
    try {
      await api.post(`/problems/${id}/unlink-ticket`, { ticket_id: ticketId });
      await refreshLinkedTickets();
      toast.success("Ticket unlinked");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally { setLinkBusy(null); }
  }

  if (loading) return <div className="text-zinc-500 p-6">Loading…</div>;
  if (error || !problem) {
    return (
      <div className="space-y-4">
        <Link to="/problems" className="text-sm text-zinc-500 hover:text-zinc-300 flex items-center gap-1.5 w-fit">
          <ArrowLeft size={14} /> Back to Problems
        </Link>
        <div className="bg-zinc-950 border border-zinc-900 rounded-md px-4 py-6 text-zinc-500 text-sm">
          Could not load this problem.
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <Link to="/problems" className="text-sm text-zinc-500 hover:text-zinc-300 flex items-center gap-1.5 w-fit">
        <ArrowLeft size={14} /> Back to Problems
      </Link>

      {isResolved && (
        <div className="bg-emerald-500/10 border border-emerald-500/30 text-emerald-300 rounded-md px-4 py-2 text-sm">
          Resolved on {fmtDateTime(problem.resolved_at)}.
        </div>
      )}

      <div className="flex items-start justify-between flex-wrap gap-3">
        <div className="min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-mono text-xs text-zinc-500">#{problem.number}</span>
            <ProblemStateBadge state={problem.state} states={states} />
          </div>
          <h1 className="font-display text-3xl font-semibold tracking-tight mt-1">{problem.title}</h1>
          <div className="text-[11px] text-zinc-500 font-mono mt-2">
            Created {fmtDate(problem.created_at)}
          </div>
        </div>
        {mayWrite && !isResolved && (
          <button onClick={() => setResolveOpen(true)} data-testid="resolve-problem-btn"
            className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-md px-3 py-1.5 text-sm flex items-center gap-1.5 shrink-0">
            <CheckCircle2 size={14} /> Resolve
          </button>
        )}
      </div>

      <div className="bg-zinc-950 border border-zinc-900 rounded-md p-4 space-y-3">
        <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-mono">Details</div>
        {isResolved ? (
          <div className="space-y-3 text-sm">
            <div><span className="text-xs text-zinc-500 block mb-1">Root cause</span>{problem.root_cause || "—"}</div>
            <div><span className="text-xs text-zinc-500 block mb-1">Workaround</span>{problem.workaround || "—"}</div>
            <div><span className="text-xs text-zinc-500 block mb-1">Resolution</span>{problem.resolution || "—"}</div>
          </div>
        ) : (
          <>
            <label className="block">
              <span className="text-xs text-zinc-400">Title</span>
              <input value={draft.title} onChange={(e) => setDraft((d) => ({ ...d, title: e.target.value }))}
                disabled={!mayWrite} data-testid="problem-title"
                className="w-full mt-1 bg-zinc-900 border border-zinc-800 rounded px-3 py-2 text-sm disabled:opacity-60 focus:outline-none focus:border-blue-500/60" />
            </label>
            <label className="block max-w-xs">
              <span className="text-xs text-zinc-400">State</span>
              <select value={draft.state} onChange={(e) => setDraft((d) => ({ ...d, state: e.target.value }))}
                disabled={!mayWrite || !states.length} data-testid="problem-state-select"
                className="w-full mt-1 bg-zinc-900 border border-zinc-800 rounded px-3 py-2 text-sm disabled:opacity-60">
                {!states.length && <option value={draft.state}>Unavailable</option>}
                {editableStates.map((s) => <option key={s.key} value={s.key}>{s.label}</option>)}
              </select>
            </label>
            <label className="block">
              <span className="text-xs text-zinc-400">Root cause</span>
              <textarea value={draft.root_cause} onChange={(e) => setDraft((d) => ({ ...d, root_cause: e.target.value }))}
                disabled={!mayWrite} rows={3} data-testid="problem-root-cause"
                className="w-full mt-1 bg-zinc-900 border border-zinc-800 rounded px-3 py-2 text-sm disabled:opacity-60 focus:outline-none focus:border-blue-500/60" />
            </label>
            <label className="block">
              <span className="text-xs text-zinc-400">Workaround</span>
              <textarea value={draft.workaround} onChange={(e) => setDraft((d) => ({ ...d, workaround: e.target.value }))}
                disabled={!mayWrite} rows={3} data-testid="problem-workaround"
                className="w-full mt-1 bg-zinc-900 border border-zinc-800 rounded px-3 py-2 text-sm disabled:opacity-60 focus:outline-none focus:border-blue-500/60" />
            </label>
            {mayWrite && (
              <button onClick={save} disabled={saving} data-testid="problem-save"
                className="bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white rounded px-4 py-1.5 text-sm">
                {saving ? "Saving…" : "Save changes"}
              </button>
            )}
          </>
        )}
      </div>

      <div className="bg-zinc-950 border border-zinc-900 rounded-md p-4 space-y-3">
        <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-mono">
          Linked tickets ({(problem.linked_tickets || []).length})
        </div>

        {mayWrite && !isResolved && (
          <div className="relative max-w-sm">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" />
            <input value={ticketQuery} onChange={(e) => setTicketQuery(e.target.value)}
              placeholder="Search tickets to link" data-testid="link-ticket-search"
              className="bg-zinc-900 border border-zinc-800 rounded-md pl-9 pr-3 py-1.5 text-sm w-full focus:outline-none focus:border-blue-500/60" />
            {ticketQuery.trim() && (
              <div className="absolute z-10 mt-1 w-full bg-zinc-950 border border-zinc-800 rounded-md shadow-lg max-h-64 overflow-y-auto">
                {searching ? <div className="px-3 py-2 text-xs text-zinc-500">Searching…</div>
                  : visibleResults.length === 0
                    ? <div className="px-3 py-2 text-xs text-zinc-500">No matching tickets.</div>
                    : visibleResults.map((t) => (
                        <button key={t.id} onClick={() => linkTicket(t.id)} disabled={linkBusy === t.id}
                          data-testid={`link-ticket-option-${t.id}`}
                          className="w-full text-left px-3 py-2 text-sm hover:bg-zinc-900/60 flex items-center justify-between gap-2 disabled:opacity-50">
                          <span className="truncate">#{t.number} {t.title}</span>
                          <Plus size={12} className="text-zinc-500 shrink-0" />
                        </button>
                      ))}
              </div>
            )}
          </div>
        )}

        {(problem.linked_tickets || []).length === 0 ? (
          <div className="text-zinc-500 text-sm">No tickets linked yet.</div>
        ) : (
          <div className="border border-zinc-900 rounded-md overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-zinc-900/50 border-b border-zinc-900 text-[10px] uppercase tracking-widest text-zinc-500 font-mono">
                <tr>
                  <th className="text-left px-4 py-2 w-20">#</th>
                  <th className="text-left px-4 py-2">Title</th>
                  <th className="text-left px-4 py-2">Status</th>
                  <th className="text-left px-4 py-2">Priority</th>
                  {mayWrite && !isResolved && <th className="text-right px-4 py-2 w-24">Action</th>}
                </tr>
              </thead>
              <tbody>
                {problem.linked_tickets.map((t) => (
                  <tr key={t.id} className="border-b border-zinc-900 hover:bg-zinc-900/40">
                    <td className="px-4 py-2 font-mono text-zinc-500 text-xs">#{t.number}</td>
                    <td className="px-4 py-2">
                      <Link to={`/tickets/${t.id}`} className="hover:text-blue-400" data-testid={`linked-ticket-${t.id}`}>{t.title}</Link>
                    </td>
                    <td className="px-4 py-2"><StatusBadge status={t.status} /></td>
                    <td className="px-4 py-2"><SeverityBadge level={t.priority} /></td>
                    {mayWrite && !isResolved && (
                      <td className="px-4 py-2 text-right">
                        <button onClick={() => unlinkTicket(t.id)} disabled={linkBusy === t.id} data-testid={`unlink-ticket-${t.id}`}
                          className="text-red-400 hover:text-red-300 text-xs flex items-center gap-1 ml-auto disabled:opacity-50">
                          <Unlink size={12} /> Unlink
                        </button>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {resolveOpen && (
        <ResolveDialog problem={problem} onClose={() => setResolveOpen(false)}
          onResolved={() => { setResolveOpen(false); load(); }} />
      )}
    </div>
  );
}

function ResolveDialog({ problem, onClose, onResolved }) {
  const [resolution, setResolution] = useState("");
  // Off by default -- posting a root-cause write-up to a customer is a
  // deliberate act, not the default outcome of resolving a problem.
  const [customerVisible, setCustomerVisible] = useState(false);
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (!resolution.trim()) return toast.error("A resolution is required.");
    setBusy(true);
    try {
      const { data } = await api.post(`/problems/${problem.id}/resolve`, {
        resolution: resolution.trim(), customer_visible: customerVisible,
      });
      const n = data?.commented_on ?? 0;
      toast.success(`Resolved. Posted to ${n} linked ticket${n === 1 ? "" : "s"}.`);
      onResolved();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally { setBusy(false); }
  };

  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent data-testid="resolve-problem-dialog" className="bg-zinc-950 border-zinc-800 text-zinc-100 max-w-lg">
        <DialogHeader><DialogTitle className="font-display">Resolve problem</DialogTitle></DialogHeader>

        <label className="block">
          <span className="text-xs text-zinc-400">Resolution</span>
          <textarea value={resolution} onChange={(e) => setResolution(e.target.value)}
            rows={5} autoFocus data-testid="resolve-resolution"
            className="w-full mt-1 bg-zinc-900 border border-zinc-800 rounded px-3 py-2 text-sm focus:outline-none focus:border-blue-500/60" />
        </label>

        <label className="flex items-start gap-2 text-sm text-zinc-300 select-none">
          <input type="checkbox" checked={customerVisible} onChange={(e) => setCustomerVisible(e.target.checked)}
            data-testid="resolve-customer-visible" className="accent-blue-600 mt-0.5" />
          <span>
            Also post this to the customer
            <div className="text-xs text-zinc-500 mt-0.5">Off by default. A root-cause write-up is drafted for engineers.</div>
          </span>
        </label>

        <DialogFooter>
          <button onClick={onClose} className="border border-zinc-800 rounded px-3 py-1.5 text-sm text-zinc-300">Cancel</button>
          <button onClick={submit} disabled={busy} data-testid="resolve-submit"
            className="bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white rounded px-4 py-1.5 text-sm">
            {busy ? "Resolving…" : "Resolve"}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
